// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Download, ArrowLeft, BarChart3, Pill, ClipboardList, Stethoscope, RefreshCcw, DollarSign, TrendingUp, AlertTriangle, Truck, CalendarDays, ShieldCheck, XCircle , Lock } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { formatNpi, formatCell, formatUsDate, isIdentifierColumn } from '../../api/format'
import { ErrorBanner, LoadingState, EmptyState, formatTimestamp } from '../../components/workflow/StatePills'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// ============================================================================
// D-UI-2 — funnel values. A column says what its cell SHOWS: money as a number rounded to
// the cents the cell prints, a percentage with its sign, a date raw.
// ============================================================================

const ISO_DATEISH = /^\d{4}-\d{2}-\d{2}(?:$|[T ]\d{2}:\d{2})/

function moneyValue(v: any): number | null {
  const n = Number(v ?? 0)
  return Number.isNaN(n) ? null : Math.round(n * 100) / 100
}
const pctValue = (v: any): string | null => (v == null ? null : `${v}%`)
const joinText = (...parts: unknown[]): string => parts.filter(p => p != null && p !== '').join(' ')

/** A saved-report cell: dates raw (the funnel compares by day), anything else as formatCell prints it. */
function savedCellValue(col: string, v: unknown): unknown {
  if (v === null || v === undefined || v === '') return null
  if (v instanceof Date || (typeof v === 'string' && ISO_DATEISH.test(v))) return v
  return formatCell(col, v)
}

// ============================================================================
// REPORTS INDEX
// ============================================================================

const REPORTS = [
  {
    // Financial close leads the list: the others are analysis, this one is the
    // record an auditor tests. Its route carries its own rx.read_financial gate.
    slug: 'month-end',
    title: 'Month-end close',
    description: 'Shipped revenue at fill grain — NDC, Rx #, fill, prescriber, payer, patient responsibility, acquisition cost and gross profit. Closing a month locks its figures so prior periods can never move.',
    icon: Lock,
  },
  {
    slug: 'rx-processed',
    title: 'Rx processed',
    description: 'Prescriptions reaching Out for Delivery or Completed, per day, with averages and a non-PHI export. Current-status snapshot — see the note on the report.',
    icon: Truck,
  },
  {
    slug: 'rx-monthly',
    title: 'Monthly throughput',
    description: 'Per month: completed, out for delivery, waiting for pickup and cancelled, with average per day — calendar days, active days and weekdays side by side.',
    icon: CalendarDays,
  },
  {
    slug: 'rx-profit',
    title: 'Rx profit & cost',
    description: 'Every prescription with acquisition cost, insurance + patient revenue, and resulting profit. Sort by margin to find your best earners.',
    icon: DollarSign,
  },
  {
    slug: 'drug-profit',
    title: 'Drug profit analysis',
    description: 'Profit aggregated by drug. Highlights drugs where insurance reimbursement is lower than cash-pay profit for the same product.',
    icon: TrendingUp,
  },
  {
    slug: 'pa-by-drug',
    title: 'Prior auth by drug',
    description: 'Drugs grouped with the insurance carriers that required prior authorization, confirmed-PA prescription counts, and average profit. Based on the PA determination engine.',
    icon: ShieldCheck,
  },
  {
    slug: 'volume',
    title: 'Volume & throughput',
    description: 'Daily Rx volume and trend over time. Useful for staffing and capacity planning.',
    icon: BarChart3,
  },
  {
    slug: 'payer-mix',
    title: 'Payer mix',
    description: 'Cash, commercial, Medicare, Medicaid breakdown. Includes PA rate and rejection rate per payer.',
    icon: ClipboardList,
  },
  {
    slug: 'controls',
    title: 'Controlled substances',
    description: 'Every controlled Rx in the period, by schedule, prescriber, and patient. Compliance officer monthly review.',
    icon: Pill,
  },
  {
    slug: 'providers',
    title: 'Provider activity',
    description: 'Rx written by prescriber, including controlled count, unique patients, and unique drugs.',
    icon: Stethoscope,
  },
  {
    slug: 'refills',
    title: 'Refill performance',
    description: 'Requests received, time-to-fulfillment, denials and reasons.',
    icon: RefreshCcw,
  },
  {
    slug: 'rx-cancelled',
    title: 'Cancelled prescriptions',
    description: 'Every cancelled prescription in the period, with drug, patient, prescriber, and cancellation date. Cancelled Rx no longer appear in the All-prescriptions list — this report is their complete capture.',
    icon: XCircle,
  },
]

export default function ReportsIndex() {
  const [saved, setSaved] = useState<api.SavedReportSummary[]>([])
  useEffect(() => { api.getMySavedReports().then(r => setSaved(r.reports || [])).catch(() => {}) }, [])
  return (
    <div style={pageStyle}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={titleStyle}>Reports</h1>
        <p style={subtitleStyle}>Monthly reviews, compliance pulls, and trend analysis. CSV export on every report.</p>
      </header>

      {saved.length > 0 && (
        <section style={{ marginBottom: 28 }} aria-label="Saved reports">
          <h2 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--color-text-soft)', margin: '0 0 12px' }}>Saved reports</h2>
          <div style={listGridStyle}>
            {saved.map(s => (
              <Link key={s.id} to={`/reports/saved/${s.id}`} style={reportCardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={iconWrapStyle}><DollarSign size={18} aria-hidden="true" /></span>
                  <strong style={{ fontSize: 16 }}>{s.name}</strong>
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-soft)', lineHeight: 1.5 }}>
                  {s.purpose_of_use} · saved {formatTimestamp(s.created_at)}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div style={listGridStyle}>
        {REPORTS.map(r => {
          const Icon = r.icon
          return (
            <Link key={r.slug} to={`/reports/${r.slug}`} style={reportCardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={iconWrapStyle}><Icon size={18} aria-hidden="true" /></span>
                <strong style={{ fontSize: 16 }}>{r.title}</strong>
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-soft)', lineHeight: 1.5 }}>
                {r.description}
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================================
// SAVED REPORT DETAIL — runs a spec saved to this pharmacy (tenant-scoped) and
// renders the masked result as a generic table.
// ============================================================================

export function SavedReportDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<api.ReportRunResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    setLoading(true); setError(null)
    api.runMySavedReport(id)
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [id])

  // D-UI-2: the columns come from the result, so the funnels are built from it too. Identity
  // columns (Rx #, NDC, NPI, phone) tick values: a Min/Max over an identifier means nothing.
  const dColumns = useMemo(() => (data?.columns ?? []).map((c): FunnelColumn<Record<string, any>> => ({
    key: c, label: c,
    ...(isIdentifierColumn(c) ? { kind: 'list' as const } : {}),
    value: row => savedCellValue(c, row[c]),
  })), [data])
  const fx = useTableFunnels(data?.rows, dColumns)
  // A new result and a new funnel selection both return to page 1.
  const dResetKey = useMemo(() => [data, fx.sig], [data, fx.sig])
  const { page: dPage, setPage: setDPage, pageSize: dPageSize, setPageSize: setDPageSize, pageRows: dPageRows, total: dTotal, totalPages: dTotalPages } = usePaged(fx.rows, DEFAULT_PAGE_SIZE, dResetKey)

  const remove = async () => {
    if (!window.confirm('Delete this saved report? This cannot be undone.')) return
    setDeleting(true)
    try { await api.deleteMySavedReport(id); navigate('/reports') }
    catch (e) { setError(friendlyError(e)); setDeleting(false) }
  }

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <button onClick={() => navigate('/reports')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 0, color: 'var(--color-text-soft)', cursor: 'pointer', fontSize: 13 }}>
          <ArrowLeft size={14} aria-hidden="true" /> Back to reports
        </button>
        <button onClick={remove} disabled={deleting} style={{ marginLeft: 'auto', background: '#fff', border: '1px solid #f0c9c9', color: '#b3261e', padding: '6px 13px', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: deleting ? 0.5 : 1 }}>
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
      {loading && <LoadingState />}
      {error && <ErrorBanner message={error} />}
      {!loading && !error && data && (
        <>
          <header style={{ marginBottom: 16 }}>
            <h1 style={titleStyle}>Saved report</h1>
            <p style={subtitleStyle}>
              {data.count.toLocaleString()} row{data.count === 1 ? '' : 's'} · {data.runtime_ms} ms
              {data.masked_columns?.length ? ` · ${data.masked_columns.length} column(s) masked for your role` : ''}
            </p>
          </header>
          {data.rows.length === 0 ? (
            <EmptyState message="No rows matched this report." />
          ) : (
            <>
            {data.rows.length === 5000 && <CapCeilingBanner />}
            <Pager page={dPage} totalPages={dTotalPages} total={dTotal} pageSize={dPageSize} onPage={setDPage} onPageSize={setDPageSize} label="rows" />
            {fx.chips}
            <div style={{ overflowX: "auto", border: '1px solid var(--color-border, #e4e9ef)', borderRadius: 8 }}>
              <table {...fx.tableProps} style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>{data.columns.map(c => (
                    <th key={c} style={{ textAlign: 'left', padding: '9px 12px', borderBottom: '1px solid #e4e9ef', background: '#fbfcfd', whiteSpace: 'nowrap' }}>{c} {fx.funnel(c)}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {dPageRows.map((row, i) => (
                    <tr key={i}>{data.columns.map(c => (
                      <td key={c} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f4f7', whiteSpace: 'nowrap' }}>
                        {row[c] === null || row[c] === undefined ? <span style={{ color: '#b0b8c0' }}>—</span> : formatCell(c, row[c])}
                      </td>
                    ))}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={dPage} totalPages={dTotalPages} total={dTotal} pageSize={dPageSize} onPage={setDPage} showPageSize={false} label="rows" />
            </>
          )}
        </>
      )}
    </div>
  )
}

// The saved-report and builder run endpoints hard-cap results at 5,000 rows server-side.
// When a result lands exactly on the cap, disclose the truncation instead of presenting
// the result as complete.
function CapCeilingBanner() {
  return (
    <div role="status" style={{ border: '1px solid #f59e0b', background: '#fffbeb', color: '#78350f', fontSize: 12.5, borderRadius: 8, padding: '7px 10px', marginBottom: 8 }}>
      This report hit the 5,000-row ceiling — rows beyond it are not shown. Narrow the report definition.
    </div>
  )
}

// ============================================================================
// REPORT DETAIL — generic shell, with per-report rendering
// ============================================================================

export function ReportDetail() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const meta = REPORTS.find(r => r.slug === slug)

  // Date range — default last 30 days
  const today = new Date()
  const thirtyAgo = new Date(today)
  thirtyAgo.setDate(thirtyAgo.getDate() - 30)
  const [from, setFrom] = useState(thirtyAgo.toISOString().slice(0, 10))
  const [to, setTo] = useState(today.toISOString().slice(0, 10))

  const [data, setData] = useState<api.ReportEnvelope<any> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return
    setLoading(true); setError(null)
    api.getReport(slug, from, to)
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [slug, from, to])

  function downloadCsv() {
    if (!data) return
    const csv = renderCsv(slug!, data)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `owlrx-${slug}-${from}-to-${to}.csv`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (!meta) return <div style={pageStyle}><EmptyState message="Unknown report." /></div>

  return (
    <div style={pageStyle}>
      <button onClick={() => navigate('/reports')} style={backBtnStyle}>
        <ArrowLeft size={14} aria-hidden="true" /> All reports
      </button>

      <header style={{ marginBottom: 16 }}>
        <h1 style={titleStyle}>{meta.title}</h1>
        <p style={subtitleStyle}>{meta.description}</p>
      </header>

      {/* Controls */}
      <div style={controlsRowStyle}>
        <label style={labelStyle}>
          From
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
        </label>
        <label style={labelStyle}>
          To
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
        </label>
        <button onClick={downloadCsv} disabled={!data || loading} style={downloadBtnStyle}>
          <Download size={13} aria-hidden="true" /> Download CSV
        </button>
      </div>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {loading ? <LoadingState label="Building report…" /> :
       !data ? null :
       <ReportBody slug={slug!} data={data} resetKey={`${slug}|${from}|${to}`} />}
    </div>
  )
}

// ============================================================================
// Report body — switches on slug
// ============================================================================

// resetKey: stable string of the inputs that change the row set (slug + date range) —
// passed to usePaged so a new report/range jumps back to page 1.
function ReportBody({ slug, data, resetKey }: { slug: string; data: api.ReportEnvelope<any>; resetKey: string }) {
  switch (slug) {
    case 'rx-processed': return <RxProcessedReport data={data} resetKey={resetKey} />
    case 'rx-monthly':   return <RxMonthlyReport data={data} />
    case 'rx-profit':   return <RxProfitReport data={data} resetKey={resetKey} />
    case 'drug-profit': return <DrugProfitReport data={data} resetKey={resetKey} />
    case 'volume':      return <VolumeReport data={data} resetKey={resetKey} />
    case 'payer-mix':   return <PayerMixReport data={data} resetKey={resetKey} />
    case 'controls':    return <ControlsReport data={data} resetKey={resetKey} />
    case 'providers':   return <ProvidersReport data={data} resetKey={resetKey} />
    case 'refills':     return <RefillsReport data={data} resetKey={resetKey} />
    case 'rx-cancelled': return <CancelledRxReport data={data} resetKey={resetKey} />
    default: return <EmptyState message="Unknown report." />
  }
}

const RX_PROFIT_COLUMNS: FunnelColumn<any>[] = [
  { key: 'date', label: 'Date', value: r => r.created_at },
  { key: 'rx', label: 'Rx #', value: r => r.rx_number, kind: 'list' },
  { key: 'drug', label: 'Drug', value: r => joinText(r.drug_name, r.drug_strength) },
  { key: 'patient', label: 'Patient', value: r => r.patient_name },
  { key: 'payer', label: 'Payer', value: r => r.insurance_carrier ?? 'Cash' },
  { key: 'cost', label: 'Cost', value: r => moneyValue(r.acquisition_cost + r.dispensing_fee) },
  { key: 'ins_paid', label: 'Ins paid', value: r => moneyValue(r.insurance_paid) },
  { key: 'pt_paid', label: 'Pt paid', value: r => moneyValue(r.patient_paid) },
  { key: 'revenue', label: 'Revenue', value: r => moneyValue(r.total_revenue) },
  { key: 'profit', label: 'Profit', value: r => moneyValue(r.profit) },
  { key: 'margin', label: 'Margin', value: r => pctValue(r.margin_pct) },
]

function RxProfitReport({ data, resetKey }: { data: api.ReportEnvelope<any>; resetKey: string }) {
  const rows = data.rows ?? []
  const s = data.summary ?? {}
  const fx = useTableFunnels(rows, RX_PROFIT_COLUMNS)
  const paged = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${resetKey}|${fx.sig}`)
  if (rows.length === 0) return <EmptyState message="No prescriptions with cost data in this period." />
  return (
    <div style={cardStyle}>
      <div style={summaryStripStyle}>
        <Stat label="Rx" value={s.count ?? 0} />
        <Stat label="Revenue"  value={fmtCurrency(s.total_revenue)} />
        <Stat label="Cost"     value={fmtCurrency(s.total_cost)} />
        <Stat label="Profit"   value={fmtCurrency(s.total_profit)} tone={Number(s.total_profit) >= 0 ? 'good' : 'bad'} />
        <Stat label="Avg margin" value={`${s.avg_margin_pct ?? 0}%`} />
      </div>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} onPageSize={paged.setPageSize} label="rows" />
      {fx.chips && <div style={chipsPadStyle}>{fx.chips}</div>}
      <table {...fx.tableProps} style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Date {fx.funnel('date')}</th>
            <th style={thStyle}>Rx # {fx.funnel('rx')}</th>
            <th style={thStyle}>Drug {fx.funnel('drug')}</th>
            <th style={thStyle}>Patient {fx.funnel('patient')}</th>
            <th style={thStyle}>Payer {fx.funnel('payer')}</th>
            <th style={thNumStyle}>Cost {fx.funnel('cost')}</th>
            <th style={thNumStyle}>Ins paid {fx.funnel('ins_paid')}</th>
            <th style={thNumStyle}>Pt paid {fx.funnel('pt_paid')}</th>
            <th style={thNumStyle}>Revenue {fx.funnel('revenue')}</th>
            <th style={thNumStyle}>Profit {fx.funnel('profit')}</th>
            <th style={thNumStyle}>Margin {fx.funnel('margin')}</th>
          </tr>
        </thead>
        <tbody>
          {paged.pageRows.map((r: any) => (
            <tr key={r.rx_id}>
              <td style={tdStyle}>{formatTimestamp(r.created_at)}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{r.rx_number ?? '—'}</td>
              <td style={tdStyle}>{r.drug_name} {r.drug_strength}</td>
              <td style={tdStyle}>{r.patient_name}</td>
              <td style={tdStyle}>{r.insurance_carrier ?? <span style={{ color: 'var(--color-text-mute)' }}>Cash</span>}</td>
              <td style={tdNumStyle}>{fmtCurrency(r.acquisition_cost + r.dispensing_fee)}</td>
              <td style={tdNumStyle}>{fmtCurrency(r.insurance_paid)}</td>
              <td style={tdNumStyle}>{fmtCurrency(r.patient_paid)}</td>
              <td style={tdNumStyle}><strong>{fmtCurrency(r.total_revenue)}</strong></td>
              <td style={{ ...tdNumStyle, color: r.profit >= 0 ? '#15803d' : '#dc2626', fontWeight: 600 }}>
                {fmtCurrency(r.profit)}
              </td>
              <td style={{ ...tdNumStyle, color: r.margin_pct >= 30 ? '#15803d' : r.margin_pct >= 15 ? 'inherit' : '#a16207' }}>
                {r.margin_pct}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} showPageSize={false} label="rows" />
    </div>
  )
}

// The first column is the highlight marker: no header, so no funnel.
const DRUG_PROFIT_COLUMNS: FunnelColumn<any>[] = [
  { key: 'drug', label: 'Drug', value: r => joinText(r.drug_name, r.drug_strength) },
  { key: 'rx_count', label: 'Rx', value: r => r.rx_count },
  { key: 'ins_cash', label: 'Ins / Cash', value: r => `${r.rx_count_insurance} / ${r.rx_count_cash}` },
  { key: 'avg_ins', label: 'Avg profit (ins)', value: r => (r.avg_profit_insurance != null ? moneyValue(r.avg_profit_insurance) : null) },
  { key: 'avg_cash', label: 'Avg profit (cash)', value: r => (r.avg_profit_cash != null ? moneyValue(r.avg_profit_cash) : null) },
  { key: 'gap', label: 'Gap', value: r => (r.profit_gap_cash_minus_ins != null ? moneyValue(r.profit_gap_cash_minus_ins) : null) },
  { key: 'total_profit', label: 'Total profit', value: r => moneyValue(r.total_profit) },
  { key: 'margin', label: 'Margin', value: r => pctValue(r.margin_pct) },
]

function DrugProfitReport({ data, resetKey }: { data: api.ReportEnvelope<any>; resetKey: string }) {
  const rows = data.rows ?? []
  const s = data.summary ?? {}
  const fx = useTableFunnels(rows, DRUG_PROFIT_COLUMNS)
  const paged = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${resetKey}|${fx.sig}`)
  if (rows.length === 0) return <EmptyState message="No drug profit data in this period." />
  return (
    <>
      <div style={cardStyle}>
        <div style={summaryStripStyle}>
          <Stat label="Distinct drugs" value={s.drug_count ?? 0} />
          <Stat label="Highlighted" value={s.highlighted_count ?? 0} tone={s.highlighted_count > 0 ? 'warn' : undefined} />
        </div>
        {s.highlighted_count > 0 && (
          <div style={highlightExplainStyle}>
            <AlertTriangle size={14} aria-hidden="true" />
            <strong>{s.highlighted_count}</strong>&nbsp;drug{s.highlighted_count === 1 ? '' : 's'} below
            show <em>higher profit on cash-pay than insurance for the same product</em>.
            That gap is potential revenue you could capture by changing payer mix or
            renegotiating the insurance contract.
          </div>
        )}
      </div>
      <div style={cardStyle}>
        <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} onPageSize={paged.setPageSize} label="rows" />
        {fx.chips && <div style={chipsPadStyle}>{fx.chips}</div>}
        <table {...fx.tableProps} style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}></th>
              <th style={thStyle}>Drug {fx.funnel('drug')}</th>
              <th style={thNumStyle}>Rx {fx.funnel('rx_count')}</th>
              <th style={thNumStyle}>Ins / Cash {fx.funnel('ins_cash')}</th>
              <th style={thNumStyle}>Avg profit (ins) {fx.funnel('avg_ins')}</th>
              <th style={thNumStyle}>Avg profit (cash) {fx.funnel('avg_cash')}</th>
              <th style={thNumStyle}>Gap {fx.funnel('gap')}</th>
              <th style={thNumStyle}>Total profit {fx.funnel('total_profit')}</th>
              <th style={thNumStyle}>Margin {fx.funnel('margin')}</th>
            </tr>
          </thead>
          <tbody>
            {paged.pageRows.map((r: any) => (
              <tr key={`${r.drug_name}|${r.drug_strength}`} style={r.highlight ? highlightRowStyle : undefined}>
                <td style={tdStyle}>
                  {r.highlight && (
                    <span title="Cash-pay profit exceeds insurance profit for this drug">
                      <AlertTriangle size={14} aria-hidden="true" style={{ color: '#a16207' }} />
                    </span>
                  )}
                </td>
                <td style={tdStyle}>
                  <strong>{r.drug_name}</strong>{' '}
                  <span style={{ color: 'var(--color-text-soft)' }}>{r.drug_strength}</span>
                  {r.is_controlled && (
                    <span style={schedBadge} title={`DEA Schedule ${r.schedule ?? 'controlled'}`}>
                      {r.schedule ?? 'CTRL'}
                    </span>
                  )}
                </td>
                <td style={tdNumStyle}><strong>{r.rx_count}</strong></td>
                <td style={tdNumStyle}>{r.rx_count_insurance} / {r.rx_count_cash}</td>
                <td style={tdNumStyle}>{r.avg_profit_insurance != null ? fmtCurrency(r.avg_profit_insurance) : '—'}</td>
                <td style={tdNumStyle}>{r.avg_profit_cash != null ? fmtCurrency(r.avg_profit_cash) : '—'}</td>
                <td style={{ ...tdNumStyle, color: r.profit_gap_cash_minus_ins != null && r.profit_gap_cash_minus_ins > 0 ? '#a16207' : 'inherit', fontWeight: r.highlight ? 600 : 400 }}>
                  {r.profit_gap_cash_minus_ins != null
                    ? (r.profit_gap_cash_minus_ins > 0 ? '+' : '') + fmtCurrency(r.profit_gap_cash_minus_ins)
                    : '—'}
                </td>
                <td style={{ ...tdNumStyle, color: r.total_profit >= 0 ? '#15803d' : '#dc2626', fontWeight: 600 }}>
                  {fmtCurrency(r.total_profit)}
                </td>
                <td style={tdNumStyle}>{r.margin_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} showPageSize={false} label="rows" />
      </div>
    </>
  )
}

function fmtCurrency(v: any): string {
  const n = Number(v ?? 0)
  if (Number.isNaN(n)) return '—'
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
}

type ProcessedRow = { day: string; out_for_delivery: number; completed: number; count: number }

const RX_MONTHLY_COLUMNS: FunnelColumn<any>[] = [
  { key: 'month', label: 'Month', value: m => m.month },
  { key: 'calendar_days', label: 'Cal. days', value: m => m.calendar_days },
  { key: 'active_days', label: 'Active days', value: m => m.active_days },
  { key: 'weekdays', label: 'Weekdays', value: m => m.weekdays },
  { key: 'completed', label: 'Completed', value: m => m.completed ?? 0 },
  { key: 'out_for_delivery', label: 'Out for delivery', value: m => m.out_for_delivery ?? 0 },
  { key: 'waiting_for_pickup', label: 'Waiting pickup', value: m => m.waiting_for_pickup ?? 0 },
  { key: 'cancelled', label: 'Cancelled', value: m => m.cancelled ?? 0 },
  { key: 'processed', label: 'Processed', value: m => m.processed ?? 0 },
  { key: 'completed_per_calendar_day', label: 'Compl./day', value: m => m.completed_per_calendar_day },
  { key: 'processed_per_calendar_day', label: 'Proc./day (cal)', value: m => m.processed_per_calendar_day },
  { key: 'processed_per_active_day', label: 'Proc./day (active)', value: m => m.processed_per_active_day },
  { key: 'processed_per_weekday', label: 'Proc./weekday', value: m => m.processed_per_weekday },
]

/**
 * D-RPT-3 — monthly throughput with honest denominators.
 *
 * Columns are the PMS's ACTUAL transaction vocabulary. ASP's PioneerRx emits no
 * "Delivered"/"Picked Up"/"Sold" — "Completed" is terminal and "Waiting for Pick up" is a
 * waiting state — so this report does not invent those columns (D-RPT-3 §rationale).
 * Three averages are shown because one alone misleads: calendar-day dilutes weekends,
 * active-day flatters a quiet month, weekday matches how a specialty pharmacy actually runs.
 */
function RxMonthlyReport({ data }: { data: api.ReportEnvelope<any> }) {
  const months: any[] = (data as any).months ?? []
  const s: any = (data as any).summary ?? {}
  const fx = useTableFunnels(months, RX_MONTHLY_COLUMNS)
  if (months.length === 0) return <EmptyState message="No transactions in this range." />
  return (
    <div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <Stat label="Completed" value={(s.completed ?? 0).toLocaleString()} />
        <Stat label="Out for delivery" value={(s.out_for_delivery ?? 0).toLocaleString()} />
        <Stat label="Waiting for pickup" value={(s.waiting_for_pickup ?? 0).toLocaleString()} />
        <Stat label="Cancelled" value={(s.cancelled ?? 0).toLocaleString()} />
        <Stat label="Months" value={String(s.months_covered ?? months.length)} />
      </div>
      {s.vocabulary_note && (
        <div style={{ fontSize: 12.5, padding: '7px 10px', marginBottom: 10, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface-raised, var(--color-surface))', color: 'var(--color-text-soft)' }}>
          {s.vocabulary_note}
        </div>
      )}
      {fx.chips}
      <div style={{ overflowX: 'auto' }}>
        <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {RX_MONTHLY_COLUMNS.map(c => (
                <th key={c.key} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>{c.label} {fx.funnel(c.key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fx.rows.map(m => (
              <tr key={m.month}>
                <td style={tdStyle}><b>{m.month}</b></td>
                <td style={tdStyle}>{m.calendar_days}</td>
                <td style={tdStyle}>{m.active_days}</td>
                <td style={tdStyle}>{m.weekdays}</td>
                <td style={tdStyle}>{(m.completed ?? 0).toLocaleString()}</td>
                <td style={tdStyle}>{(m.out_for_delivery ?? 0).toLocaleString()}</td>
                <td style={tdStyle}>{(m.waiting_for_pickup ?? 0).toLocaleString()}</td>
                <td style={tdStyle}>{(m.cancelled ?? 0).toLocaleString()}</td>
                <td style={tdStyle}><b>{(m.processed ?? 0).toLocaleString()}</b></td>
                <td style={tdStyle}>{m.completed_per_calendar_day}</td>
                <td style={tdStyle}>{m.processed_per_calendar_day}</td>
                <td style={tdStyle}>{m.processed_per_active_day}</td>
                <td style={tdStyle}><b>{m.processed_per_weekday}</b></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

type ProcessedMonth = { total: number; ofd: number; done: number; days: number }
// One definition for the cell and for its funnel, so the option a user ticks is the text shown.
const processedMonthLabel = (key: string) => formatUsDate(`${key}-01`).replace(/^(\d+)\/\d+\//, '$1/')
const processedMonthAvg = (m: ProcessedMonth) => (m.days > 0 ? Math.round((m.total / m.days) * 10) / 10 : 0)

const PROCESSED_MONTH_COLUMNS: FunnelColumn<[string, ProcessedMonth]>[] = [
  { key: 'month', label: 'Month', value: ([key]) => processedMonthLabel(key) },
  { key: 'total', label: 'Processed', value: ([, m]) => m.total },
  { key: 'ofd', label: 'Out for delivery', value: ([, m]) => m.ofd },
  { key: 'done', label: 'Completed', value: ([, m]) => m.done },
  { key: 'days', label: 'Days', value: ([, m]) => m.days },
  { key: 'avg', label: 'Avg / active day', value: ([, m]) => processedMonthAvg(m) },
]
const PROCESSED_DAY_COLUMNS: FunnelColumn<ProcessedRow>[] = [
  { key: 'day', label: 'Day', value: r => r.day },
  { key: 'count', label: 'Processed', value: r => r.count },
  { key: 'out_for_delivery', label: 'Out for delivery', value: r => r.out_for_delivery },
  { key: 'completed', label: 'Completed', value: r => r.completed },
]

function RxProcessedReport({ data, resetKey }: { data: api.ReportEnvelope<ProcessedRow>; resetKey: string }) {
  const rows = data.rows ?? []
  const s = data.summary ?? {}
  // Monthly rollup from the daily rows — no second query. Always the FULL range: the daily
  // table's funnels do not change it. Built above the early return because its own funnels
  // are a hook.
  const monthRows = useMemo(() => {
    const months = new Map<string, ProcessedMonth>()
    for (const r of data.rows ?? []) {
      const key = r.day.slice(0, 7)
      const m = months.get(key) ?? { total: 0, ofd: 0, done: 0, days: 0 }
      m.total += r.count; m.ofd += r.out_for_delivery; m.done += r.completed; m.days += 1
      months.set(key, m)
    }
    return Array.from(months.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [data.rows])
  const fxMonth = useTableFunnels(monthRows, PROCESSED_MONTH_COLUMNS)
  const fx = useTableFunnels(rows, PROCESSED_DAY_COLUMNS)
  // Daily table only — the bar chart and the monthly rollup need the full range.
  const paged = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${resetKey}|${fx.sig}`)
  if (rows.length === 0) return <EmptyState message="No prescriptions reached Out for Delivery or Completed in this date range." />

  const max = Math.max(...rows.map(r => r.count), 1)

  return (
    <div style={cardStyle}>
      <div style={{
        display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 16px',
        background: '#fef3c7', borderBottom: '1px solid #fde68a',
      }}>
        <AlertTriangle size={16} style={{ color: '#a16207', flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, lineHeight: 1.6, color: '#78350f' }}>
          <strong>Current-status snapshot.</strong> Each Rx is counted on the day it last changed
          status, so days older than about a week understate actual volume. Reliable for the
          current week; not a historical event log.
        </div>
      </div>

      <div style={summaryStripStyle}>
        <Stat label="Total processed" value={s.total ?? 0} />
        <Stat label="Avg / day" value={s.avg_per_calendar_day ?? 0} />
        <Stat label="Out for delivery" value={s.out_for_delivery ?? 0} />
        <Stat label="Completed" value={s.completed ?? 0} />
        <Stat label="Days with activity" value={s.active_days ?? 0} />
      </div>

      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120 }}>
          {rows.map(r => (
            <div key={r.day} title={`${formatUsDate(r.day)} — ${r.count} processed (${r.out_for_delivery} out for delivery, ${r.completed} completed)`}
                 style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
              <div style={{ height: `${(r.completed / max) * 100}%`, background: '#eb6834', minHeight: r.completed > 0 ? 2 : 0 }} />
              <div style={{ height: `${(r.out_for_delivery / max) * 100}%`, background: '#2a78d6', minHeight: r.out_for_delivery > 0 ? 2 : 0 }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11, color: 'var(--color-text-mute)' }}>
          <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#2a78d6', marginRight: 5 }} />Out for delivery</span>
          <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#eb6834', marginRight: 5 }} />Completed</span>
        </div>
      </div>

      {monthRows.length > 1 && (
        <>
        {fxMonth.chips && <div style={chipsPadStyle}>{fxMonth.chips}</div>}
        <table {...fxMonth.tableProps} style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Month {fxMonth.funnel('month')}</th>
              <th style={thNumStyle}>Processed {fxMonth.funnel('total')}</th>
              <th style={thNumStyle}>Out for delivery {fxMonth.funnel('ofd')}</th>
              <th style={thNumStyle}>Completed {fxMonth.funnel('done')}</th>
              <th style={thNumStyle}>Days {fxMonth.funnel('days')}</th>
              <th style={thNumStyle}>Avg / active day {fxMonth.funnel('avg')}</th>
            </tr>
          </thead>
          <tbody>
            {fxMonth.rows.map(([key, m]) => (
              <tr key={key}>
                <td style={tdStyle}>{processedMonthLabel(key)}</td>
                <td style={tdNumStyle}><strong>{m.total}</strong></td>
                <td style={tdNumStyle}>{m.ofd}</td>
                <td style={tdNumStyle}>{m.done}</td>
                <td style={tdNumStyle}>{m.days}</td>
                <td style={tdNumStyle}>{processedMonthAvg(m)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}

      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} onPageSize={paged.setPageSize} label="rows" />
      {fx.chips && <div style={chipsPadStyle}>{fx.chips}</div>}
      <table {...fx.tableProps} style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Day {fx.funnel('day')}</th>
            <th style={thNumStyle}>Processed {fx.funnel('count')}</th>
            <th style={thNumStyle}>Out for delivery {fx.funnel('out_for_delivery')}</th>
            <th style={thNumStyle}>Completed {fx.funnel('completed')}</th>
          </tr>
        </thead>
        <tbody>
          {paged.pageRows.map(r => (
            <tr key={r.day}>
              <td style={tdStyle}>{formatUsDate(r.day)}</td>
              <td style={tdNumStyle}><strong>{r.count}</strong></td>
              <td style={tdNumStyle}>{r.out_for_delivery}</td>
              <td style={tdNumStyle}>{r.completed}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} showPageSize={false} label="rows" />

      <div style={{ padding: '10px 16px', fontSize: 11, color: 'var(--color-text-mute)', borderTop: '1px solid var(--color-border)' }}>
        Export includes {(data.detail ?? []).length} prescriptions — Rx number, drug, status,
        processed date, refill number, days supply. No patient identifiers.
        {data.detail_truncated && (
          <strong style={{ color: '#a16207' }}> Capped at {data.detail_limit} rows — narrow the date range for a complete export.</strong>
        )}
      </div>
    </div>
  )
}

const VOLUME_COLUMNS: FunnelColumn<{ day: string; count: number }>[] = [
  { key: 'day', label: 'Day', value: r => r.day },
  { key: 'count', label: 'Rx', value: r => r.count },
]

function VolumeReport({ data, resetKey }: { data: api.ReportEnvelope<{ day: string; count: number }>; resetKey: string }) {
  const rows = data.rows ?? []
  // Daily table only — the bar chart needs the full range.
  const fx = useTableFunnels(rows, VOLUME_COLUMNS)
  const paged = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${resetKey}|${fx.sig}`)
  if (rows.length === 0) return <EmptyState message="No prescriptions in this date range." />
  const total = rows.reduce((a, r) => a + r.count, 0)
  const avg = Math.round(total / rows.length)
  const max = Math.max(...rows.map(r => r.count), 1)
  return (
    <div style={cardStyle}>
      <div style={summaryStripStyle}>
        <Stat label="Total Rx" value={total} />
        <Stat label="Days" value={rows.length} />
        <Stat label="Avg / day" value={avg} />
        <Stat label="Peak day" value={Math.max(...rows.map(r => r.count))} />
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120 }}>
          {rows.map(r => (
            <div key={r.day} title={`${r.day}: ${r.count}`}
                 style={{
                   flex: 1, height: `${(r.count / max) * 100}%`,
                   background: 'var(--color-primary, #0d9488)', minHeight: r.count > 0 ? 2 : 0,
                 }} />
          ))}
        </div>
      </div>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} onPageSize={paged.setPageSize} label="rows" />
      {fx.chips && <div style={chipsPadStyle}>{fx.chips}</div>}
      <table {...fx.tableProps} style={tableStyle}>
        <thead>
          <tr><th style={thStyle}>Day {fx.funnel('day')}</th><th style={thNumStyle}>Rx {fx.funnel('count')}</th></tr>
        </thead>
        <tbody>
          {paged.pageRows.map(r => (
            <tr key={r.day}>
              <td style={tdStyle}>{r.day}</td>
              <td style={tdNumStyle}><strong>{r.count}</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} showPageSize={false} label="rows" />
    </div>
  )
}

function PayerMixReport({ data, resetKey }: { data: api.ReportEnvelope<any>; resetKey: string }) {
  const rows = data.rows ?? []
  // % of total stays computed from the FULL row set, not the visible page.
  const total = rows.reduce((a: number, r: any) => a + r.count, 0)
  const columns = useMemo<FunnelColumn<any>[]>(() => [
    { key: 'payer', label: 'Payer', value: r => r.payer },
    { key: 'count', label: 'Rx', value: r => r.count },
    { key: 'pct', label: '% of total', value: r => `${((r.count / total) * 100).toFixed(1)}%` },
    { key: 'pa_count', label: 'PA needed', value: r => r.pa_count },
    { key: 'pa_rate', label: 'PA rate', value: r => pctValue(r.pa_rate) },
    { key: 'rejected_count', label: 'Rejected', value: r => r.rejected_count },
    { key: 'rejection_rate', label: 'Rejection rate', value: r => pctValue(r.rejection_rate) },
  ], [total])
  const fx = useTableFunnels(rows, columns)
  const paged = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${resetKey}|${fx.sig}`)
  if (rows.length === 0) return <EmptyState message="No data for this date range." />
  return (
    <div style={cardStyle}>
      <div style={summaryStripStyle}>
        <Stat label="Total Rx" value={total} />
        <Stat label="Distinct payers" value={rows.length} />
      </div>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} onPageSize={paged.setPageSize} label="rows" />
      {fx.chips && <div style={chipsPadStyle}>{fx.chips}</div>}
      <table {...fx.tableProps} style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Payer {fx.funnel('payer')}</th>
            <th style={thNumStyle}>Rx {fx.funnel('count')}</th>
            <th style={thNumStyle}>% of total {fx.funnel('pct')}</th>
            <th style={thNumStyle}>PA needed {fx.funnel('pa_count')}</th>
            <th style={thNumStyle}>PA rate {fx.funnel('pa_rate')}</th>
            <th style={thNumStyle}>Rejected {fx.funnel('rejected_count')}</th>
            <th style={thNumStyle}>Rejection rate {fx.funnel('rejection_rate')}</th>
          </tr>
        </thead>
        <tbody>
          {paged.pageRows.map((r: any) => (
            <tr key={r.payer}>
              <td style={tdStyle}>{r.payer}</td>
              <td style={tdNumStyle}><strong>{r.count}</strong></td>
              <td style={tdNumStyle}>{((r.count / total) * 100).toFixed(1)}%</td>
              <td style={tdNumStyle}>{r.pa_count}</td>
              <td style={tdNumStyle}>{r.pa_rate}%</td>
              <td style={tdNumStyle}>{r.rejected_count}</td>
              <td style={{ ...tdNumStyle, color: r.rejection_rate > 5 ? '#a16207' : 'inherit' }}>
                {r.rejection_rate}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} showPageSize={false} label="rows" />
    </div>
  )
}

const CONTROLS_COLUMNS: FunnelColumn<any>[] = [
  { key: 'date', label: 'Date', value: r => r.created_at },
  { key: 'rx', label: 'Rx #', value: r => r.rx_number, kind: 'list' },
  { key: 'drug', label: 'Drug', value: r => joinText(r.drug_name, r.drug_strength) },
  { key: 'schedule', label: 'Schedule', value: r => r.schedule ?? 'CTRL' },
  { key: 'qty', label: 'Qty', value: r => joinText(r.quantity, r.unit) },
  { key: 'patient', label: 'Patient', value: r => r.patient_name },
  { key: 'prescriber', label: 'Prescriber', value: r => r.prescriber_name },
  { key: 'state', label: 'State', value: r => r.state },
]

function ControlsReport({ data, resetKey }: { data: api.ReportEnvelope<any>; resetKey: string }) {
  const rows = data.rows ?? []
  // Schedule counts in the summary strip stay computed from the FULL row set.
  const fx = useTableFunnels(rows, CONTROLS_COLUMNS)
  const paged = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${resetKey}|${fx.sig}`)
  if (rows.length === 0) return <EmptyState message="No controlled substance prescriptions in this period." />
  return (
    <div style={cardStyle}>
      <div style={summaryStripStyle}>
        <Stat label="Total" value={rows.length} />
        <Stat label="C-II" value={rows.filter((r: any) => r.schedule === 'CII').length} />
        <Stat label="C-III" value={rows.filter((r: any) => r.schedule === 'CIII').length} />
        <Stat label="C-IV" value={rows.filter((r: any) => r.schedule === 'CIV').length} />
      </div>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} onPageSize={paged.setPageSize} label="rows" />
      {fx.chips && <div style={chipsPadStyle}>{fx.chips}</div>}
      <table {...fx.tableProps} style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Date {fx.funnel('date')}</th>
            <th style={thStyle}>Rx # {fx.funnel('rx')}</th>
            <th style={thStyle}>Drug {fx.funnel('drug')}</th>
            <th style={thStyle}>Schedule {fx.funnel('schedule')}</th>
            <th style={thStyle}>Qty {fx.funnel('qty')}</th>
            <th style={thStyle}>Patient {fx.funnel('patient')}</th>
            <th style={thStyle}>Prescriber {fx.funnel('prescriber')}</th>
            <th style={thStyle}>State {fx.funnel('state')}</th>
          </tr>
        </thead>
        <tbody>
          {paged.pageRows.map((r: any) => (
            <tr key={r.rx_id}>
              <td style={tdStyle}>{formatTimestamp(r.created_at)}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{r.rx_number ?? '—'}</td>
              <td style={tdStyle}>{r.drug_name} {r.drug_strength}</td>
              <td style={tdStyle}><span style={schedBadge}>{r.schedule ?? 'CTRL'}</span></td>
              <td style={tdStyle}>{r.quantity} {r.unit}</td>
              <td style={tdStyle}>{r.patient_name}</td>
              <td style={tdStyle}>{r.prescriber_name}</td>
              <td style={tdStyle}>{r.state}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} showPageSize={false} label="rows" />
    </div>
  )
}

const CANCELLED_RX_COLUMNS: FunnelColumn<any>[] = [
  { key: 'cancelled', label: 'Cancelled', value: r => r.cancelled_at },
  { key: 'rx', label: 'Rx #', value: r => r.rx_number, kind: 'list' },
  { key: 'drug', label: 'Drug', value: r => joinText(r.drug_name, r.drug_strength) },
  { key: 'patient', label: 'Patient', value: r => r.patient_name },
  { key: 'prescriber', label: 'Prescriber', value: r => r.prescriber_name },
  { key: 'written', label: 'Written', value: r => r.created_at },
]

// ClickUp 86cb22vg0: the All-Rx tab excludes cancelled — this report is their capture.
function CancelledRxReport({ data, resetKey }: { data: api.ReportEnvelope<any>; resetKey: string }) {
  const rows = data.rows ?? []
  const fx = useTableFunnels(rows, CANCELLED_RX_COLUMNS)
  const paged = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${resetKey}|${fx.sig}`)
  if (rows.length === 0) return <EmptyState message="No prescriptions were cancelled in this period." />
  return (
    <div style={cardStyle}>
      <div style={summaryStripStyle}>
        <Stat label="Cancelled Rx" value={rows.length} />
        <Stat label="Unique patients" value={new Set(rows.map((r: any) => r.patient_name)).size} />
        <Stat label="Unique drugs" value={new Set(rows.map((r: any) => r.drug_name)).size} />
      </div>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} onPageSize={paged.setPageSize} label="rows" />
      {fx.chips && <div style={chipsPadStyle}>{fx.chips}</div>}
      <table {...fx.tableProps} style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Cancelled {fx.funnel('cancelled')}</th>
            <th style={thStyle}>Rx # {fx.funnel('rx')}</th>
            <th style={thStyle}>Drug {fx.funnel('drug')}</th>
            <th style={thStyle}>Patient {fx.funnel('patient')}</th>
            <th style={thStyle}>Prescriber {fx.funnel('prescriber')}</th>
            <th style={thStyle}>Written {fx.funnel('written')}</th>
          </tr>
        </thead>
        <tbody>
          {paged.pageRows.map((r: any) => (
            <tr key={r.rx_id}>
              <td style={tdStyle}>{formatTimestamp(r.cancelled_at)}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{r.rx_number ?? '—'}</td>
              <td style={tdStyle}>{r.drug_name} {r.drug_strength}</td>
              <td style={tdStyle}>{r.patient_name ?? '—'}</td>
              <td style={tdStyle}>{r.prescriber_name ?? '—'}</td>
              <td style={tdStyle}>{formatTimestamp(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} showPageSize={false} label="rows" />
    </div>
  )
}

const PROVIDERS_COLUMNS: FunnelColumn<any>[] = [
  { key: 'prescriber', label: 'Prescriber', value: r => r.prescriber_name },
  { key: 'specialty', label: 'Specialty', value: r => r.specialty },
  { key: 'npi', label: 'NPI', value: r => formatNpi(r.npi), kind: 'list' },
  { key: 'rx_count', label: 'Rx', value: r => r.rx_count },
  { key: 'controlled_count', label: 'Controlled', value: r => r.controlled_count },
  { key: 'unique_patients', label: 'Unique patients', value: r => r.unique_patients },
  { key: 'unique_drugs', label: 'Unique drugs', value: r => r.unique_drugs },
]

function ProvidersReport({ data, resetKey }: { data: api.ReportEnvelope<any>; resetKey: string }) {
  const rows = data.rows ?? []
  const fx = useTableFunnels(rows, PROVIDERS_COLUMNS)
  const paged = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${resetKey}|${fx.sig}`)
  if (rows.length === 0) return <EmptyState message="No provider activity in this date range." />
  return (
    <div style={cardStyle}>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} onPageSize={paged.setPageSize} label="rows" />
      {fx.chips && <div style={chipsPadStyle}>{fx.chips}</div>}
      <table {...fx.tableProps} style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Prescriber {fx.funnel('prescriber')}</th>
            <th style={thStyle}>Specialty {fx.funnel('specialty')}</th>
            <th style={thStyle}>NPI {fx.funnel('npi')}</th>
            <th style={thNumStyle}>Rx {fx.funnel('rx_count')}</th>
            <th style={thNumStyle}>Controlled {fx.funnel('controlled_count')}</th>
            <th style={thNumStyle}>Unique patients {fx.funnel('unique_patients')}</th>
            <th style={thNumStyle}>Unique drugs {fx.funnel('unique_drugs')}</th>
          </tr>
        </thead>
        <tbody>
          {paged.pageRows.map((r: any) => (
            <tr key={r.prescriber_id}>
              <td style={tdStyle}>{r.prescriber_name}</td>
              <td style={tdStyle}>{r.specialty ?? '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{formatNpi(r.npi)}</td>
              <td style={tdNumStyle}><strong>{r.rx_count}</strong></td>
              <td style={tdNumStyle}>{r.controlled_count}</td>
              <td style={tdNumStyle}>{r.unique_patients}</td>
              <td style={tdNumStyle}>{r.unique_drugs}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} showPageSize={false} label="rows" />
    </div>
  )
}

const DENIAL_COLUMNS: FunnelColumn<{ reason: string; count: number }>[] = [
  // Free text, so a contains box rather than a tick list of every sentence.
  { key: 'reason', label: 'Reason', value: d => d.reason, kind: 'text' },
  { key: 'count', label: 'Count', value: d => d.count },
]

function RefillsReport({ data, resetKey }: { data: api.ReportEnvelope<any>; resetKey: string }) {
  const s = data.summary ?? {}
  const denials = data.denial_reasons ?? []
  // Denial reasons can be free-text, so the list grows with the range.
  const fx = useTableFunnels(denials, DENIAL_COLUMNS)
  const paged = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${resetKey}|${fx.sig}`)
  return (
    <>
      <div style={cardStyle}>
        <div style={summaryStripStyle}>
          <Stat label="Total requests" value={s.total ?? 0} />
          <Stat label="Fulfilled" value={s.fulfilled ?? 0} />
          <Stat label="Denied" value={s.denied ?? 0} />
          <Stat label="Pending" value={s.pending ?? 0} />
          <Stat label="Avg fulfill time" value={`${Math.round((s.avg_fulfill_min ?? 0) / 60)}h`} />
        </div>
      </div>
      <div style={cardStyle}>
        <header style={cardHeaderStyle}>
          <h3 style={cardTitleStyle}>Denial reasons</h3>
        </header>
        {denials.length === 0
          ? <div style={{ padding: 16 }}><EmptyState message="No denials in this period." /></div>
          : (
            <>
            <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} onPageSize={paged.setPageSize} label="rows" />
            {fx.chips && <div style={chipsPadStyle}>{fx.chips}</div>}
            <table {...fx.tableProps} style={tableStyle}>
              <thead><tr><th style={thStyle}>Reason {fx.funnel('reason')}</th><th style={thNumStyle}>Count {fx.funnel('count')}</th></tr></thead>
              <tbody>
                {paged.pageRows.map(d => (
                  <tr key={d.reason}>
                    <td style={tdStyle}>{d.reason}</td>
                    <td style={tdNumStyle}><strong>{d.count}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} showPageSize={false} label="rows" />
            </>
          )}
      </div>
    </>
  )
}

function Stat({ label, value, tone }: { label: string; value: any; tone?: 'good' | 'bad' | 'warn' }) {
  const color =
    tone === 'good' ? '#15803d' :
    tone === 'bad'  ? '#dc2626' :
    tone === 'warn' ? '#a16207' :
    'var(--color-text)'
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>{label}</div>
    </div>
  )
}

// ============================================================================
// CSV rendering
// ============================================================================

function renderCsv(slug: string, data: api.ReportEnvelope<any>): string {
  const rows = data.rows ?? []
  // D-RPT-1: exports the Rx-level detail array, not the daily aggregate.
  // Non-PHI column set only — no name, DOB, address, MRN, prescriber, or payer.
  if (slug === 'rx-processed') return toCsv(
    ['Rx #', 'Drug', 'Status', 'Processed date', 'Refill #', 'Days supply'],
    (data.detail ?? []).map((r: any) => [
      r.rx_number, r.drug_name, r.tx_status, formatUsDate(r.processed_date), r.refill_number, r.days_supply,
    ]),
  )
  if (slug === 'rx-monthly') return toCsv(
    ['Month', 'Calendar days', 'Active days', 'Weekdays', 'Completed', 'Out for delivery',
     'Waiting for pickup', 'Cancelled', 'Processed', 'Completed/day (cal)',
     'Processed/day (cal)', 'Processed/day (active)', 'Processed/weekday'],
    ((data as any).months ?? []).map((m: any) => [
      m.month, m.calendar_days, m.active_days, m.weekdays, m.completed, m.out_for_delivery,
      m.waiting_for_pickup, m.cancelled, m.processed, m.completed_per_calendar_day,
      m.processed_per_calendar_day, m.processed_per_active_day, m.processed_per_weekday,
    ]),
  )
  if (slug === 'rx-profit') return toCsv(
    ['Date', 'Rx #', 'Drug', 'Strength', 'Patient', 'Insurance', 'Cost', 'Insurance paid', 'Patient paid', 'Revenue', 'Profit', 'Margin %'],
    rows.map((r: any) => [
      r.created_at, r.rx_number ?? '', r.drug_name, r.drug_strength,
      r.patient_name, r.insurance_carrier ?? 'Cash',
      Number(r.acquisition_cost) + Number(r.dispensing_fee),
      r.insurance_paid, r.patient_paid, r.total_revenue, r.profit, r.margin_pct,
    ]))
  if (slug === 'drug-profit') return toCsv(
    ['Drug', 'Strength', 'Schedule', 'Rx', 'Rx (insurance)', 'Rx (cash)',
     'Avg profit (insurance)', 'Avg profit (cash)', 'Profit gap (cash − ins)',
     'Total profit', 'Margin %', 'Highlight'],
    rows.map((r: any) => [
      r.drug_name, r.drug_strength, r.schedule ?? '',
      r.rx_count, r.rx_count_insurance, r.rx_count_cash,
      r.avg_profit_insurance ?? '', r.avg_profit_cash ?? '',
      r.profit_gap_cash_minus_ins ?? '',
      r.total_profit, r.margin_pct, r.highlight ? 'YES' : '',
    ]))
  if (slug === 'volume')   return toCsv(['Day', 'Rx count'], rows.map((r: any) => [r.day, r.count]))
  if (slug === 'payer-mix') return toCsv(
    ['Payer', 'Rx', 'PA needed', 'PA rate %', 'Rejected', 'Rejection rate %'],
    rows.map((r: any) => [r.payer, r.count, r.pa_count, r.pa_rate, r.rejected_count, r.rejection_rate]))
  if (slug === 'controls') return toCsv(
    ['Date', 'Rx #', 'Drug', 'Strength', 'Schedule', 'Qty', 'Unit', 'Patient', 'Prescriber', 'NPI', 'State'],
    rows.map((r: any) => [r.created_at, r.rx_number ?? '', r.drug_name, r.drug_strength, r.schedule ?? '', r.quantity, r.unit, r.patient_name, r.prescriber_name, formatNpi(r.prescriber_npi).replace('Not provided',''), r.state]))
  if (slug === 'rx-cancelled') return toCsv(
    ['Cancelled at', 'Rx #', 'Drug', 'Strength', 'Patient', 'Prescriber', 'Written at'],
    rows.map((r: any) => [
      r.cancelled_at, r.rx_number ?? '', r.drug_name ?? '', r.drug_strength ?? '',
      r.patient_name ?? '', r.prescriber_name ?? '', r.created_at,
    ]))
  if (slug === 'providers') return toCsv(
    ['Prescriber', 'Specialty', 'NPI', 'Rx', 'Controlled', 'Unique patients', 'Unique drugs'],
    rows.map((r: any) => [r.prescriber_name, r.specialty ?? '', formatNpi(r.npi).replace('Not provided',''), r.rx_count, r.controlled_count, r.unique_patients, r.unique_drugs]))
  if (slug === 'refills') {
    const s = data.summary ?? {}
    const lines = [
      'Metric,Value',
      `Total requests,${s.total ?? 0}`,
      `Fulfilled,${s.fulfilled ?? 0}`,
      `Denied,${s.denied ?? 0}`,
      `Pending,${s.pending ?? 0}`,
      `Avg fulfill minutes,${s.avg_fulfill_min ?? 0}`,
      '',
      'Denial reason,Count',
      ...((data.denial_reasons ?? []).map(d => `${csvCell(d.reason)},${d.count}`))
    ]
    return lines.join('\n')
  }
  return ''
}

function toCsv(headers: string[], rows: any[][]): string {
  return [headers.join(','), ...rows.map(r => r.map(csvCell).join(','))].join('\n')
}
function csvCell(v: any): string {
  if (v === null || v === undefined) return ''
  const s = String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

// ============================================================================
// Styles
// ============================================================================

const pageStyle: React.CSSProperties = { maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', padding: '24px 24px 48px' }
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = { marginTop: 6, color: 'var(--color-text-soft)', fontSize: 14 }
const listGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12,
}
const reportCardStyle: React.CSSProperties = {
  display: 'block', padding: 16,
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, textDecoration: 'none', color: 'inherit',
}
const iconWrapStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 32, height: 32, background: '#f0fdfa', color: 'var(--color-primary, #0d9488)',
  borderRadius: 6,
}
const backBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
  background: 'transparent', border: '1px solid var(--color-border)',
  borderRadius: 6, fontSize: 12, color: 'var(--color-text-soft)',
  cursor: 'pointer', fontFamily: 'inherit', marginBottom: 16,
}
const controlsRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 16,
}
const labelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11,
  textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-text-mute)', fontWeight: 600,
}
const inputStyle: React.CSSProperties = {
  padding: '6px 10px', fontSize: 13, border: '1px solid var(--color-border)',
  borderRadius: 6, fontFamily: 'inherit',
}
const downloadBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '8px 14px', fontSize: 13, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, marginBottom: 16, overflow: 'hidden',
}
const cardHeaderStyle: React.CSSProperties = {
  padding: '12px 14px', background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-soft)',
}
const cardTitleStyle: React.CSSProperties = {
  margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
}
const summaryStripStyle: React.CSSProperties = {
  display: 'flex', gap: 24, padding: 16,
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-alt)',
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
// The report cards have no padding of their own; the funnel chips need the table's gutter.
const chipsPadStyle: React.CSSProperties = { padding: '0 14px' }
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
  color: 'var(--color-text-mute)',
}
const thNumStyle: React.CSSProperties = { ...thStyle, textAlign: 'right' as const }
const tdStyle: React.CSSProperties = { padding: '10px 14px', borderBottom: '1px solid var(--color-border)' }
const tdNumStyle: React.CSSProperties = { ...tdStyle, textAlign: 'right' as const }
const schedBadge: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
  padding: '1px 6px', background: '#fef3c7', color: '#854d0e', borderRadius: 3,
  marginLeft: 6,
}
const highlightRowStyle: React.CSSProperties = {
  background: '#fffbeb',
}
const highlightExplainStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '12px 16px',
  background: '#fffbeb', color: '#854d0e',
  borderTop: '1px solid var(--color-border)',
  fontSize: 13, lineHeight: 1.5,
}

// ============================================================================
// PRIOR AUTH BY DRUG — dedicated aggregate report (D-PA-1 downstream).
// Param-driven (group toggle, sortable columns, min-Rx floor), not date-ranged.
// Data: PA state from the comment engine; insurance from insurance_carrier;
// profit real (total_price_paid - ingredient_cost_paid). Read-only.
// ============================================================================

const PA_STATUS_LABEL: Record<string, string> = { no_pa: 'No PA', pa_required: 'PA required', mixed: 'Mixed' }
const PA_STATUS_STYLE: Record<string, React.CSSProperties> = {
  pa_required: { background: '#fef2f2', color: '#b91c1c' },
  no_pa:       { background: '#f0fdf4', color: '#15803d' },
  mixed:       { background: '#fffbeb', color: '#b45309' },
}

// Keys are the server's sort columns, so a header sorts and filters under one name.
const PA_BY_DRUG_COLUMNS: FunnelColumn<api.PaByDrugRow>[] = [
  { key: 'drug_name', label: 'Drug', value: r => r.drug_name },
  { key: 'generic_name', label: 'Generic', value: r => r.generic_name },
  { key: 'carrier', label: 'Insurance', value: r => r.carrier },
  { key: 'pa_status', label: 'PA status', value: r => PA_STATUS_LABEL[r.pa_status] || r.pa_status },
  { key: 'pa_confirmed', label: 'Confirmed PA Rx', value: r => r.pa_confirmed },
  { key: 'total_rx', label: 'Total Rx', value: r => r.total_rx },
  { key: 'avg_profit', label: 'Avg profit $', value: r => (r.avg_profit == null ? null : moneyValue(r.avg_profit)) },
  { key: 'avg_margin_pct', label: 'Avg margin %', value: r => pctValue(r.avg_margin_pct) },
]

export function PaByDrugReport() {
  const navigate = useNavigate()
  const [group, setGroup] = useState<'drug' | 'molecule'>('drug')
  const [orderBy, setOrderBy] = useState('pa_confirmed')
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('desc')
  const [minRx, setMinRx] = useState(1)
  const [drugFilter, setDrugFilter] = useState('')
  const [carrierFilter, setCarrierFilter] = useState('')

  const [data, setData] = useState<api.PaByDrugEnvelope | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true); setError(null)
    api.getPaByDrugReport({ group, orderBy, orderDir, minRx })
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [group, orderBy, orderDir, minRx])

  // Memoised so the funnels see a new array only when the data or a text filter changes.
  const rows = useMemo(() => (data?.rows || []).filter(r =>
    (!drugFilter || r.drug_name.toLowerCase().includes(drugFilter.toLowerCase())) &&
    (!carrierFilter || r.carrier.toLowerCase().includes(carrierFilter.toLowerCase()))),
  [data, drugFilter, carrierFilter])
  // The header funnels AND-compose with the two text filters: they re-slice `rows`.
  const fx = useTableFunnels(rows, PA_BY_DRUG_COLUMNS)

  // Every input that changes the row set — server params + client filters + funnels — resets to page 1.
  const paged = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${group}|${orderBy}|${orderDir}|${minRx}|${drugFilter}|${carrierFilter}|${fx.sig}`)

  function sortBy(col: string) {
    if (orderBy === col) setOrderDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setOrderBy(col); setOrderDir('desc') }
  }
  function sortArrow(col: string) { return orderBy === col ? (orderDir === 'asc' ? ' ▲' : ' ▼') : '' }

  function downloadCsv() {
    if (!rows.length) return
    const header = ['Drug', 'Generic', 'Insurance', 'PA status', 'Confirmed PA Rx', 'PA required', 'PA pending', 'PA denied', 'Total Rx', 'Avg profit $', 'Avg margin %']
    const body = rows.map(r => [r.drug_name, r.generic_name, r.carrier, PA_STATUS_LABEL[r.pa_status] || r.pa_status,
      r.pa_confirmed, r.pa_required, r.pa_pending, r.pa_denied, r.total_rx,
      r.avg_profit == null ? '' : r.avg_profit, r.avg_margin_pct == null ? '' : r.avg_margin_pct])
    const csv = toCsv(header, body)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = 'owlrx-pa-by-drug.csv'
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
  }

  const th: React.CSSProperties = { textAlign: 'left', padding: '9px 10px', fontSize: 12, color: 'var(--color-text-soft)', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }
  const thNum: React.CSSProperties = { ...th, textAlign: 'right' }
  const td: React.CSSProperties = { padding: '9px 10px', fontSize: 13, borderTop: '1px solid var(--color-border)' }
  const tdNum: React.CSSProperties = { ...td, textAlign: 'right' }

  function profitColor(p: number | null): string | undefined {
    if (p == null) return undefined
    return p >= 25 ? '#15803d' : p < 15 ? '#b45309' : undefined
  }

  return (
    <div style={pageStyle}>
      <button onClick={() => navigate('/reports')} style={backBtnStyle}>
        <ArrowLeft size={14} aria-hidden="true" /> All reports
      </button>
      <header style={{ marginBottom: 16 }}>
        <h1 style={titleStyle}>Prior auth by drug</h1>
        <p style={subtitleStyle}>
          Drugs and the insurance carriers that required prior authorization, with confirmed-PA
          prescription counts and average profit. PA status comes from the determination engine;
          only prescriptions with active PA evidence (required, pending, or denied) count as confirmed PA.
        </p>
      </header>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <div style={labelStyle}>Group by
          <div style={{ display: 'inline-flex', border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden', marginTop: 4 }}>
            {(['drug', 'molecule'] as const).map(g => (
              <button key={g} onClick={() => setGroup(g)}
                style={{ padding: '7px 12px', fontSize: 13, border: 'none', cursor: 'pointer',
                  background: group === g ? 'var(--color-accent, #2563eb)' : 'transparent',
                  color: group === g ? '#fff' : 'var(--color-text)' }}>
                {g === 'drug' ? 'Drug name' : 'Molecule'}
              </button>
            ))}
          </div>
        </div>
        <label style={labelStyle}>Min Rx
          <input type="number" min={1} value={minRx} onChange={e => setMinRx(Math.max(1, Number(e.target.value) || 1))} style={{ ...inputStyle, width: 80 }} />
        </label>
        <label style={labelStyle}>Filter drug
          <input value={drugFilter} onChange={e => setDrugFilter(e.target.value)} placeholder="name…" style={inputStyle} />
        </label>
        <label style={labelStyle}>Filter insurance
          <input value={carrierFilter} onChange={e => setCarrierFilter(e.target.value)} placeholder="carrier…" style={inputStyle} />
        </label>
        <button onClick={downloadCsv} disabled={!rows.length} style={downloadBtnStyle}>
          <Download size={13} aria-hidden="true" /> Download CSV
        </button>
      </div>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {loading ? <LoadingState label="Building report…" /> : !data ? null : (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 18 }}>
            <SummaryCard label="Drugs with confirmed PA" value={data.summary.drugs_with_confirmed_pa} />
            <SummaryCard label="Rx with PA confirmed" value={data.summary.rx_pa_confirmed} />
            <SummaryCard label="PA denied" value={data.summary.pa_denied} />
            <SummaryCard label="Still outstanding" value={data.summary.pa_outstanding} />
          </div>

          {rows.length === 0 ? <EmptyState message="No drugs with confirmed prior authorization for the current filters." /> : (
            <>
            <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} onPageSize={paged.setPageSize} label="rows" />
            {fx.chips}
            <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 10 }}>
              <table {...fx.tableProps} style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th} onClick={() => sortBy('drug_name')}>Drug{sortArrow('drug_name')} {fx.funnel('drug_name')}</th>
                    <th style={th} onClick={() => sortBy('generic_name')}>Generic{sortArrow('generic_name')} {fx.funnel('generic_name')}</th>
                    <th style={th} onClick={() => sortBy('carrier')}>Insurance{sortArrow('carrier')} {fx.funnel('carrier')}</th>
                    <th style={th} onClick={() => sortBy('pa_status')}>PA status{sortArrow('pa_status')} {fx.funnel('pa_status')}</th>
                    <th style={thNum} onClick={() => sortBy('pa_confirmed')}>Confirmed PA Rx{sortArrow('pa_confirmed')} {fx.funnel('pa_confirmed')}</th>
                    <th style={thNum} onClick={() => sortBy('total_rx')}>Total Rx{sortArrow('total_rx')} {fx.funnel('total_rx')}</th>
                    <th style={thNum} onClick={() => sortBy('avg_profit')}>Avg profit ${sortArrow('avg_profit')} {fx.funnel('avg_profit')}</th>
                    <th style={thNum} onClick={() => sortBy('avg_margin_pct')}>%{sortArrow('avg_margin_pct')} {fx.funnel('avg_margin_pct')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.pageRows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ ...td, fontWeight: 500 }}>{r.drug_name}</td>
                      <td style={{ ...td, color: 'var(--color-text-soft)' }}>{r.generic_name}</td>
                      <td style={td}>{r.carrier}</td>
                      <td style={td}>
                        <span style={{ ...PA_STATUS_STYLE[r.pa_status], fontSize: 12, padding: '2px 9px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                          {PA_STATUS_LABEL[r.pa_status] || r.pa_status}
                        </span>
                      </td>
                      <td style={{ ...tdNum, fontWeight: 500 }}>{r.pa_confirmed}</td>
                      <td style={{ ...tdNum, color: 'var(--color-text-soft)' }}>{r.total_rx}</td>
                      <td style={tdNum}>{r.avg_profit == null ? '—' : `$${r.avg_profit.toFixed(2)}`}</td>
                      <td style={{ ...tdNum, color: profitColor(r.avg_margin_pct) }}>{r.avg_margin_pct == null ? '—' : `${r.avg_margin_pct}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={paged.page} totalPages={paged.totalPages} total={paged.total} pageSize={paged.pageSize} onPage={paged.setPage} showPageSize={false} label="rows" />
            </>
          )}
          <p style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 10 }}>
            {/* fx.rows.length is the filtered total (text filters + funnels, not the visible page), so this stays honest with pagination. */}
            {fx.rows.length} of {data.row_count} drug × insurance rows. Profit is insurance + patient revenue minus drug cost, averaged per group; “—” means cost data was not available. Margin is coloured green above 25%, amber below 15%.
          </p>
        </>
      )}
    </div>
  )
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 13, color: 'var(--color-text-soft)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600 }}>{(value ?? 0).toLocaleString()}</div>
    </div>
  )
}
